/**
 * PATH: packages/ui/src/kanban/components/KanbanCardActionLine.tsx
 * FILE_ID: UI-KANBAN-CARD-ACTION-LINE-001
 * PURPOSE: The card face's "action spine" (expanded density): the next-action
 *   note + a pressable, filled action-date chip on one row, with a whispered
 *   "was <prev>" history sub-line. Plus the blocked state strip.
 * WHY: Card design v2 (operator 2026-07-07, canvas kanban-card-design-v2):
 *   action_note — "the single most operational field on the card" — was
 *   drawer-only, so the board couldn't answer "what happens next" without
 *   opening cards; and a BLOCKED card looked identical to a live one (the
 *   reason was buried in the drawer property list). Both fields already ride
 *   the lean list payload (BE lean.py serves action_note truncated to 160)
 *   — this is a render-only change.
 * FLOW:
 *   ┌──────────────────────┐   ┌────────────────┐   ┌────────────────────┐
 *   │ card.action_note /   │ → │ self-gating    │ → │ note + date chip   │
 *   │ action_date / status │   │ pure gates     │   │ + was-line + state │
 *   └──────────────────────┘   └────────────────┘   └────────────────────┘
 * RELATED:
 *   - ./KanbanCard.tsx (renders both, expanded/large only).
 *   - ./KanbanCardDateChip.tsx (the click-to-edit swap).
 *   - ./KanbanCardDuePills.tsx (previousDateLine — one gate, not two).
 */
import { Target } from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'
import { formatDmMonthShort } from '../utils/localDate'
import { KanbanCardDateChip } from './KanbanCardDateChip'
import { previousDateLine } from './KanbanCardDuePills'

/** Pure gate: the action line renders when the card carries a next-action
 *  note or an action date. */
export function actionLineVisible(
  card: KanbanCardModel,
  show: { note?: boolean; date?: boolean } = {},
): boolean {
  const showNote = show.note !== false
  const showDate = show.date !== false
  return !!((showNote && card.action_note?.trim()) || (showDate && card.action_date))
}

/** Pure gate: the state strip renders only for a BLOCKED card (snoozed
 *  already has the footer pill + 60% opacity; access-locked has its own
 *  face). Returns the strip text, or null. */
export function stateStripText(card: KanbanCardModel): string | null {
  if (card.status !== 'BLOCKED') return null
  const reason = card.blocked_reason?.trim()
  return reason ? `Blocked — ${reason}` : 'Blocked'
}

export interface KanbanCardActionLineProps {
  card: KanbanCardModel
  /** Same signature as the quick-action bundle's onSetActionDate. Omitted →
   *  the chip renders static (read-only host). */
  onSetActionDate?: (
    card: KanbanCardModel,
    actionDate: string | null,
  ) => Promise<unknown> | void
  showNote?: boolean
  showDate?: boolean
  showPrevious?: boolean
}

/**
 * The action spine row: accent bar + note (1-line truncate) + filled
 * action-date chip, then the "was <prev>" history whisper. Self-gating.
 */
export function KanbanCardActionLine({
  card,
  onSetActionDate,
  showNote = true,
  showDate = true,
  showPrevious = true,
}: KanbanCardActionLineProps) {
  if (!actionLineVisible(card, { note: showNote, date: showDate })) return null
  const note = showNote ? card.action_note?.trim() ?? '' : ''
  const prev = showPrevious
    ? previousDateLine(card.action_date, card.previous_action_date)
    : null

  return (
    <div className="mt-1" data-testid="kanban-card-action-line">
      <div className="flex items-center gap-1.5">
        {/* Accent bar — marks the row as THE next action without a second
            filled surface competing with the date chip. */}
        <span
          aria-hidden
          className="h-3.5 w-[3px] shrink-0 rounded-full bg-blue-500/80 dark:bg-blue-400/80"
        />
        {note ? (
          <span
            className="min-w-0 flex-1 truncate text-[11.5px] leading-snug text-neutral-600 dark:text-zinc-300"
            title={note}
          >
            {note}
          </span>
        ) : (
          <span className="min-w-0 flex-1" />
        )}
        {showDate && card.action_date && (
          <KanbanCardDateChip
            value={card.action_date}
            ariaLabel={`Action date ${formatDmMonthShort(card.action_date)}`}
            onCommit={
              onSetActionDate
                ? (iso) => onSetActionDate(card, iso)
                : undefined
            }
            className="shrink-0"
          >
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-1.5 py-px text-[10px] font-semibold leading-4 text-white dark:bg-blue-500">
              <Target className="h-2.5 w-2.5" aria-hidden />
              {formatDmMonthShort(card.action_date)}
            </span>
          </KanbanCardDateChip>
        )}
      </div>
      {/* History whisper — smaller, indented, struck through. One gate
          (previousDateLine) shared with the footer pills so a no-op PATCH
          echo can't draw a strikethrough of itself. */}
      {prev && (
        <div
          className="pl-[9px] text-[9.5px] leading-tight text-red-500/90 line-through dark:text-red-400/90"
          title={`Previously ${formatDmMonthShort(prev)}`}
        >
          was {formatDmMonthShort(prev)}
        </div>
      )}
    </div>
  )
}

/** The blocked state strip — a card that can't move must not look live.
 *  Also the inbox-chip fact, when the chip and this lane disagree.
 *  That fact is text only. It does not move the card. */
export function KanbanCardStateStrip({ card }: { card: KanbanCardModel }) {
  const text = stateStripText(card)
  const chip = card.chip_lane_note?.trim() ?? ''
  if (!text && !chip) return null
  return (
    <>
      {text && (
        <div
          className="mt-1 truncate rounded bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          data-testid="kanban-card-state-strip"
          title={text}
        >
          {text}
        </div>
      )}
      {chip && (
        <div
          className="mt-1 truncate text-[10.5px] text-neutral-500 dark:text-zinc-400"
          data-testid="kanban-card-chip-lane-note"
          title={chip}
        >
          {chip}
        </div>
      )}
    </>
  )
}

